import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { Translation, getI18n } from 'react-i18next';
import {
  Affix,
  Card,
  Col,
  Form,
  Input,
  Row,
  Spin,
  Radio,
  Switch,
  InputNumber,
  Select,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import message from '../../../elements/lib/MessageWrapper';
import FormMetadata from '../../../elements/components/FormMetadata';
import { pathTo } from '../../../Routes';
import Logger from '../../../../../lib/Logger';
import PartnershipInput from '../../partnerships/containers/PartnershipInputContainer';
import CognitoFormPublicStatusInput from './CognitoFormPublicStatusInput';
import RegistrationCodeInput from '../../registrationCodes/containers/RegistrationCodeInputContainer';
import UserInput from '../../users/containers/UserInputContainer';
import CareGuidePartnerTagInput from '../../careGuidePartnerTags/containers/CareGuidePartnerTagInputContainer';

const optionsTypes = [
  {
    label: getI18n().t('cognito_form_onboarding_top'),
    value: 1,
  },
  {
    label: getI18n().t('cognito_form_internal_forms'),
    value: 2,
  },
  {
    label: getI18n().t('cognito_form_surveys'),
    value: 3,
  },
];

const CognitoFormForm = ({
  id,
  data,
  errors,
  load,
  destroyForm,
  isLoading,
  isSubmitting,
  created_id,
  ...props
}) => {
  const [redirectTo, setRedirectTo] = useState(null);
  const [form] = Form.useForm();
  const [type, setType] = useState(1);
  const [didInitialize, setDidInitialize] = useState(false);
  const [switchNewUsers, setSwitchNewUsers] = useState(false);
  const [switchOldUsers, setSwitchOldUsers] = useState(false);
  const [oldUserRequirement, setOldUserRequirement] = useState(null);
  const [oldUserHowOften, setOldUserHowOften] = useState(null);

  // form column settings
  const layout = {
    main: {
      labelCol: { span: 5 },
      wrapperCol: { span: 19 },
    },
  };

  // load record data from API
  useEffect(() => {
    if (id) {
      load(id);
    }
  }, [id, load]);

  // update input values when new data is available
  const dataString = JSON.stringify(data);
  useEffect(() => {
    if (id && !isSubmitting && !didInitialize) {
      const dataObj = JSON.parse(dataString);

      const old_user_requirement = dataObj['old_user_requirement'];

      setSwitchNewUsers(dataObj['new_user_requirement'] ? true : false);
      setSwitchOldUsers(old_user_requirement ? true : false);
      setOldUserRequirement(old_user_requirement);
      setOldUserHowOften(dataObj['old_user_requirement_frequency_type']);

      setType(dataObj['type']);
      const careGuidePartnerTagIds = dataObj[
        'cognito_forms_care_guide_partner_tags'
      ].map((tag) => tag.id);
      dataObj['cognito_forms_care_guide_partner_tags'] = careGuidePartnerTagIds;
      form.setFieldsValue(dataObj);
      setDidInitialize(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, data, isSubmitting, id, didInitialize]);

  // handle errors reported by API
  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{ name: field, errors: errors[field] }]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    form.scrollToField(firstFieldName);
  }, [form, errors]);

  // redirect add form to edit form on successful create action
  useEffect(() => {
    if (created_id) {
      setRedirectTo(pathTo('CognitoFormEditScreen', { id: created_id }));
    }
    return () => {
      destroyForm();
    };
  }, [created_id, setRedirectTo, destroyForm]);

  // submit data handler
  const submitData = async (values) => {
    Logger.log('debug', `CognitoFormForm.submitData(###)`);

    // API POST/PUT payload
    let payload = {};
    let cognito_forms_care_guide_partner_tags = [];

    for (const input of Object.keys(data)) {
      if (input in values) {
        if (['registration_code'].includes(input)) {
          payload[input + '_id'] = values[input];
        } else if (['profile'].includes(input)) {
          payload['user_' + input + '_id'] = values[input];
        } else if (input === 'cognito_forms_care_guide_partner_tags') {
          if (Array.isArray(values[input])) {
            const tagsFilters = values[input].filter((item) => item !== '');
            cognito_forms_care_guide_partner_tags =
              cognito_forms_care_guide_partner_tags.concat(tagsFilters);
          } else {
            if (values[input] !== '') {
              cognito_forms_care_guide_partner_tags.push(values[input]);
            }
          }
        } else {
          payload[input] = values[input];
        }
      }
    }

    payload['cognito_forms_care_guide_partner_tags_list'] =
      cognito_forms_care_guide_partner_tags;
    delete payload['cognito_forms_care_guide_partner_tags'];

    if (id) {
      // update
      props.update(id, payload, (success) => {
        if (success) {
          message.success(getI18n().t('feedback_form_success'));
        } else {
          message.error(getI18n().t('feedback_form_error'));
        }
      });
    } else {
      // create
      props.create(payload, (success) => {
        if (success) {
          message.success(getI18n().t('feedback_form_success'));
        } else {
          message.error(getI18n().t('feedback_form_error'));
        }
      });
    }
  };

  // form submit handler
  const handleFinish = async (values) => {
    Logger.log('debug', `CognitoFormForm.handleFinish(###)`);
    if (!props.isSubmitting) {
      await submitData(values);
    }
  };

  // form error handler
  const handleFinishFailed = ({ values, errorFields, outOfDate }) => {
    Logger.log('debug', `CognitoFormForm.handleFinishFailed(###)`);
    message.error(getI18n().t('feedback_form_error'));
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name);
    }
  };

  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  return (
    <Translation>
      {(t) => (
        <>
          <div className='cognito-forms-form'>
            <Form
              name='cognito_forms_form'
              form={form}
              initialValues={data}
              onFinish={handleFinish}
              onFinishFailed={handleFinishFailed}
              validateTrigger='onSubmit'
              {...layout.main}
            >
              <Row gutter={16}>
                <Col xs={24} lg={18}>
                  <Card
                    title={
                      id
                        ? t('cognito_form_edit_title')
                        : t('cognito_form_add_title')
                    }
                    extra={
                      isLoading ? (
                        <Spin
                          indicator={
                            <LoadingOutlined style={{ fontSize: 20 }} spin />
                          }
                        />
                      ) : null
                    }
                  >
                    <PartnershipInput
                      form={form}
                      name='partnership_id'
                      label={t('cognito_form_partnership')}
                      disabled={isLoading || isSubmitting}
                      rules={[
                        {
                          required: true,
                          message: t('feedback_validation_required'),
                        },
                      ]}
                    />
                    <UserInput
                      form={form}
                      name='profile'
                      label={t('cognito_form_profile')}
                      rules={[
                        {
                          required: true,
                          message: t('feedback_validation_required'),
                        },
                      ]}
                      disabled={isLoading || isSubmitting}
                    />

                    <div className='form-group'>
                      <Form.Item
                        name='name'
                        label={t('cognito_form_name')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                          {
                            type: 'string',
                            min: 2,
                            max: 100,
                            message: t('feedback_validation_char_range', {
                              min: 1,
                              max: 100,
                            }),
                          },
                        ]}
                      >
                        <Input disabled={isLoading || isSubmitting} />
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        name='form_id'
                        label={t('cognito_form_form_id')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                          {
                            type: 'string',
                            min: 1,
                            max: 10,
                            message: t('feedback_validation_char_range', {
                              min: 1,
                              max: 10,
                            }),
                          },
                        ]}
                      >
                        <Input disabled={isLoading || isSubmitting} />
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        name='form_key'
                        label={t('cognito_form_form_key')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                          {
                            type: 'string',
                            min: 1,
                            max: 40,
                            message: t('feedback_validation_char_range', {
                              min: 1,
                              max: 40,
                            }),
                          },
                        ]}
                      >
                        <Input disabled={isLoading || isSubmitting} />
                      </Form.Item>
                    </div>
                    <div className='form-group'>
                      <CognitoFormPublicStatusInput
                        name='public_status'
                        label={t('cognito_form_public_status')}
                        disabled={isLoading || isSubmitting}
                      />
                    </div>

                    <div className='form-group'>
                      <Form.Item name='type' label={t('cognito_form_type')}>
                        <Radio.Group
                          options={optionsTypes}
                          onChange={(e) => setType(e.target.value)}
                        />
                      </Form.Item>
                    </div>
                    {type === 1 && (
                      <>
                        <RegistrationCodeInput
                          name='registration_code'
                          rules={[
                            {
                              required: true,
                              message: t('feedback_validation_required'),
                            },
                          ]}
                          disabled={isLoading || isSubmitting}
                          label={t('cognito_form_registration_code')}
                          filter={{ type_user: [3] }}
                        />
                        <div className='form-group'>
                          <Form.Item
                            name={null}
                            label={t('cognito_form_new_users')}
                            style={{ marginBottom: '0' }}
                          >
                            <Switch
                              checked={switchNewUsers}
                              onChange={setSwitchNewUsers}
                            />
                          </Form.Item>
                        </div>

                        {switchNewUsers && (
                          <div className='form-group'>
                            <Form.Item
                              name='new_user_requirement'
                              label={t('cognito_form_option_new_user')}
                              style={{ marginTop: '16px', marginBottom: '0' }}
                            >
                              <Radio.Group>
                                <Radio value={1}>
                                  {t('cognito_form_new_user_required')}
                                </Radio>
                                <Radio value={2}>
                                  {t('cognito_form_new_user_optional')}
                                </Radio>
                              </Radio.Group>
                            </Form.Item>
                          </div>
                        )}

                        <div className='form-group'>
                          <Form.Item
                            name={null}
                            label={t('cognito_form_old_users')}
                            style={{ marginTop: '16px', marginBottom: '0' }}
                          >
                            <Switch
                              checked={switchOldUsers}
                              onChange={setSwitchOldUsers}
                            />
                          </Form.Item>
                        </div>

                        {switchOldUsers && (
                          <>
                            <div className='form-group'>
                              <Form.Item
                                name='old_user_requirement'
                                label={t('cognito_form_option_old_user')}
                                style={{
                                  marginTop: '16px',
                                  marginBottom: '12px',
                                }}
                              >
                                <Radio.Group
                                  onChange={(e) =>
                                    setOldUserRequirement(e.target.value)
                                  }
                                >
                                  <Radio value={1}>
                                    {t('cognito_form_old_user_required')}
                                  </Radio>
                                  <Radio value={2}>
                                    {t('cognito_form_old_user_optional')}
                                  </Radio>
                                </Radio.Group>
                              </Form.Item>
                            </div>

                            {oldUserRequirement === 1 && (
                              <>
                                <div className='form-group'>
                                  <Form.Item
                                    name='old_user_requirement_frequency_type'
                                    label={t('cognito_form_old_user_how_often')}
                                    style={{ marginBottom: '12px' }}
                                  >
                                    <Radio.Group
                                      onChange={(e) =>
                                        setOldUserHowOften(e.target.value)
                                      }
                                    >
                                      <Radio value={1}>
                                        {t('cognito_form_old_user_custom')}
                                      </Radio>
                                      <Radio value={2}>
                                        {t('cognito_form_old_user_login')}
                                      </Radio>
                                    </Radio.Group>
                                  </Form.Item>
                                </div>

                                {oldUserHowOften === 1 && (
                                  <div className='form-group'>
                                    <Form.Item
                                      label={t(
                                        'cognito_form_label_repeat_every'
                                      )}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                          marginTop: '4px',
                                        }}
                                      >
                                        <div className='form-group'>
                                          <Form.Item
                                            name='old_user_requirement_repeat_every'
                                            label={null}
                                            style={{ marginBottom: 0 }}
                                          >
                                            <InputNumber
                                              min={1}
                                              style={{ width: '60px' }}
                                            />
                                          </Form.Item>
                                        </div>

                                        <div className='form-group'>
                                          <Form.Item
                                            name='old_user_requirement_repeat_unit'
                                            label={null}
                                            style={{ marginBottom: 0 }}
                                          >
                                            <Select
                                              defaultValue={1}
                                              style={{ width: 100 }}
                                              options={[
                                                {
                                                  value: 1,
                                                  label: 'Day(s)',
                                                },
                                                {
                                                  value: 2,
                                                  label: 'Week(s)',
                                                },
                                              ]}
                                            />
                                          </Form.Item>
                                        </div>
                                      </div>
                                    </Form.Item>
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}

                    {type === 2 && (
                      <CareGuidePartnerTagInput
                        name='cognito_forms_care_guide_partner_tags'
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                        disabled={isLoading || isSubmitting}
                        label={t('cognito_form_care_guide_partner_tags')}
                      />
                    )}
                  </Card>
                </Col>

                <Col xs={24} lg={6}>
                  <Affix offsetTop={10}>
                    <Card title={t('form_metadata_header')}>
                      <FormMetadata
                        id={id}
                        isSubmitting={isSubmitting}
                        delete={props.delete.bind(this)}
                        deleteRedirectTo='CognitoFormsScreen'
                        createdAt={props.createdAt}
                        updatedAt={props.updatedAt}
                      />
                    </Card>
                  </Affix>
                </Col>
              </Row>
            </Form>
          </div>
        </>
      )}
    </Translation>
  );
};

export default CognitoFormForm;

Logger.log('silly', `CognitoFormForm loaded.`);
